'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  FileCheck,
  CheckCircle,
  Wallet,
  Send,
  Calendar,
  Mail,
  Gift,
  BarChart3,
  type LucideIcon,
} from 'lucide-react';

interface WorkflowStep {
  number: number;
  title: string;
  description: string;
  icon: LucideIcon;
}

interface Workflow {
  id: string;
  title: string;
  subtitle: string;
  accentColor: string;
  steps: WorkflowStep[];
}

const workflows: Workflow[] = [
  {
    id: 'batch-payments',
    title: 'Batch Payments',
    subtitle: 'Process hundreds of payments in minutes',
    accentColor: 'from-[#1B5E20] to-[#2E7D32]',
    steps: [
      {
        number: 1,
        title: 'Upload Batch',
        description: 'Upload payment files via CSV or configure batches through the intuitive dashboard interface with built-in validation.',
        icon: FileCheck,
      },
      {
        number: 2,
        title: 'Review & Approve',
        description: 'Multi-level approval workflows ensure compliance, accuracy, and proper authorization before any funds move.',
        icon: CheckCircle,
      },
      {
        number: 3,
        title: 'Process Payments',
        description: 'Automated processing across multiple mobile money providers with real-time status updates and error handling.',
        icon: Wallet,
      },
      {
        number: 4,
        title: 'Track & Reconcile',
        description: 'Real-time tracking dashboard and automated reconciliation reports match disbursements with bank statements.',
        icon: Send,
      },
    ],
  },
  {
    id: 'ceremonies',
    title: 'Ceremonies & Contributions',
    subtitle: 'Manage events and collect contributions seamlessly',
    accentColor: 'from-[#2E7D32] to-[#4CAF50]',
    steps: [
      {
        number: 1,
        title: 'Create Event',
        description: 'Set up your ceremony with details, date, venue, and contribution goals. Customize invitation templates to match your event.',
        icon: Calendar,
      },
      {
        number: 2,
        title: 'Send Invitations',
        description: 'Distribute beautiful digital invitation cards to your guest list via SMS, email, or WhatsApp with personalized designs.',
        icon: Mail,
      },
      {
        number: 3,
        title: 'Collect Contributions',
        description: 'Guests contribute automatically via mobile money. No manual follow-ups needed — track every contribution in real-time.',
        icon: Gift,
      },
      {
        number: 4,
        title: 'Track & Manage',
        description: 'Monitor RSVP responses, contribution progress, and event metrics from a comprehensive real-time dashboard.',
        icon: BarChart3,
      },
    ],
  },
];

function WorkflowColumn({
  workflow,
  index,
  visible,
}: {
  workflow: Workflow;
  index: number;
  visible: boolean;
}) {
  return (
    <div
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: visible ? `${index * 200}ms` : '0ms' }}
    >
      <div className="mb-8">
        <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${workflow.accentColor} rounded-full px-4 py-1.5 mb-4`}>
          <span className="text-xs font-semibold text-white tracking-wide">
            {workflow.title}
          </span>
        </div>
        <h3 className="text-xl font-bold text-foreground mb-1">{workflow.subtitle}</h3>
      </div>

      <div className="relative space-y-6">
        <div className="absolute left-[27px] top-6 bottom-6 w-0.5 bg-border" />

        {workflow.steps.map((step, stepIndex) => {
          const Icon = step.icon;
          return (
            <div
              key={step.number}
              className={`relative flex items-start gap-5 transition-all duration-500 ${
                visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}
              style={{ transitionDelay: visible ? `${index * 200 + stepIndex * 100 + 300}ms` : '0ms' }}
            >
              <div className="relative flex-shrink-0 z-10">
                <div className="w-14 h-14 rounded-full bg-[#4CAF50]/10 border-2 border-[#1B5E20]/20 flex items-center justify-center">
                  <div className="w-9 h-9 rounded-full bg-[#1B5E20] flex items-center justify-center">
                    <Icon size={16} className="text-white" />
                  </div>
                </div>
                <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#4CAF50] text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
                  {step.number}
                </span>
              </div>

              <div className="flex-1 pt-1">
                <h4 className="text-base font-bold text-foreground mb-1">{step.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DetailedHowItWorks() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
      <div className="max-w-6xl mx-auto">
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            How It Works
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Two powerful workflows designed to streamline your operations from start to finish
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {workflows.map((workflow, index) => (
            <WorkflowColumn
              key={workflow.id}
              workflow={workflow}
              index={index}
              visible={visible}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
